import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { apiClient } from "../App";
import { usePermissions } from "../contexts/PermissionsContext";
import { toast } from "sonner";
import { RefreshCw, Wand2, List, CalendarDays, Scale } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import MoneyKPIs from "../components/money/MoneyKPIs";
import ReceivablesTable from "../components/money/ReceivablesTable";
import MonthlyOutlookTable from "../components/money/MonthlyOutlookTable";
import AgingBuckets from "../components/money/AgingBuckets";
import EditReceivableDialog from "../components/money/EditReceivableDialog";
import MilestonesOverview from "../components/money/MilestonesOverview";
import MoneyCalendarView from "../components/money/MoneyCalendarView";
import ReconcileDialog from "../components/money/ReconcileDialog";

const MODES = [
  { key: "past_due", label: "Past Due", shortLabel: "Past Due" },
  { key: "this_month", label: "This Month", shortLabel: "Month" },
  { key: "outlook_3m", label: "3M", shortLabel: "3M" },
  { key: "outlook_6m", label: "6M", shortLabel: "6M" }
];

function formatCompact(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

export default function MoneyView() {
  const { canViewFinancials, loading: permsLoading } = usePermissions();
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const [mode, setMode] = useState("this_month");
  const [summary, setSummary] = useState(null);
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [editingReceivable, setEditingReceivable] = useState(null);
  const [users, setUsers] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const outlookMonths = mode === "outlook_6m" ? 6 : 3;
      const summaryRes = await apiClient.get(`/receivables/summary?outlook_months=${outlookMonths}`);
      setSummary(summaryRes.data);
      
      const apiMode = selectedMonth ? "all" : mode;
      const monthParam = selectedMonth ? `&month=${selectedMonth}` : "";
      const receivablesRes = await apiClient.get(`/receivables?mode=${apiMode}${monthParam}`);
      setReceivables(receivablesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [mode, selectedMonth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    apiClient.get("/users").then(res => setUsers(res.data)).catch(() => {});
  }, []);

  async function handleGenerateReceivables() {
    setGenerating(true);
    try {
      const response = await apiClient.post("/receivables/generate-from-programs");
      toast.success(`Generated ${response.data.created} receivables`, {
        description: `${response.data.skipped} programs already had receivables`
      });
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate");
    } finally {
      setGenerating(false);
    }
  }

  function handleModeChange(newMode) {
    setSelectedMonth(null);
    setMode(newMode);
  }

  function formatSubtotals() {
    if (!receivables.length) return null;
    const expected = receivables.reduce((sum, r) => sum + r.expected_amount, 0);
    const collected = receivables.reduce((sum, r) => sum + r.collected_amount, 0);
    const outstanding = receivables.reduce((sum, r) => sum + r.outstanding, 0);
    return { expected, collected, outstanding };
  }

  const subtotals = formatSubtotals();
  const showOutlook = mode === "outlook_3m" || mode === "outlook_6m";
  const showAging = mode === "past_due" && summary?.past_due_total > 0;

  // Financial lock: PMs / non-financial roles cannot open this page, even by
  // typing the URL directly (the sidebar link is already hidden for them).
  if (permsLoading || loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!canViewFinancials) {
    return <Navigate to="/" replace />;
  }

  return (
    <div data-testid="money-view" className="pb-20 md:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-4xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Money
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1">
            Receivables & collection outlook
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center rounded-lg border bg-muted p-0.5">
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-7 sm:h-8 px-2 sm:px-3 rounded-md"
              data-testid="money-list-view-btn"
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">List</span>
            </Button>
            <Button
              variant={viewMode === "calendar" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("calendar")}
              className="h-7 sm:h-8 px-2 sm:px-3 rounded-md"
              data-testid="money-calendar-view-btn"
            >
              <CalendarDays className="w-4 h-4" />
              <span className="hidden sm:inline ml-1.5">Calendar</span>
            </Button>
          </div>
          
          {viewMode === "list" && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="h-8 sm:h-9 text-xs sm:text-sm"
              >
                <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${refreshing ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReconciling(true)}
                className="h-8 sm:h-9 text-xs sm:text-sm"
                data-testid="money-reconcile-btn"
              >
                <Scale className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline ml-2">Reconcile</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateReceivables}
                disabled={generating}
                className="h-8 sm:h-9 text-xs sm:text-sm"
              >
                <Wand2 className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${generating ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline ml-2">{generating ? "Generating..." : "Generate"}</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <MoneyCalendarView />
      )}

      {/* List View (Original Content) */}
      {viewMode === "list" && (
        <>
          {/* KPI Tiles */}
          {summary && <MoneyKPIs summary={summary} />}

          {/* Filter Mode Pills */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-4 sm:mb-6 overflow-x-auto pb-1">
            {MODES.map((m) => (
              <Button
                key={m.key}
                variant={mode === m.key ? "default" : "outline"}
                size="sm"
                onClick={() => handleModeChange(m.key)}
                className="rounded-full h-8 sm:h-9 text-xs sm:text-sm px-3 sm:px-4 flex-shrink-0"
              >
                <span className="sm:hidden">{m.shortLabel}</span>
                <span className="hidden sm:inline">{m.label}</span>
              </Button>
            ))}
            {selectedMonth && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedMonth(null)}
                className="text-muted-foreground text-xs sm:text-sm h-8 sm:h-9 flex-shrink-0"
              >
                Clear: {selectedMonth}
              </Button>
            )}
          </div>

          {/* Aging Buckets (Past Due mode) */}
          {showAging && (
            <AgingBuckets 
              agingBuckets={summary?.aging_buckets} 
              pastDueTotal={summary?.past_due_total} 
            />
          )}

          {/* Monthly Outlook (3M/6M modes) */}
          {showOutlook && summary?.monthly_outlook && (
            <MonthlyOutlookTable
              monthlyOutlook={summary.monthly_outlook}
              selectedMonth={selectedMonth}
              onSelectMonth={setSelectedMonth}
            />
          )}

          {/* Milestones Overview Section */}
          <div className="mt-8 mb-6">
            <MilestonesOverview />
          </div>

          {/* Reconcile against the payment-milestone ledger */}
          <ReconcileDialog
            open={reconciling}
            onClose={() => setReconciling(false)}
            onApplied={() => fetchData(true)}
          />

          {/* Edit Dialog */}
          {editingReceivable && (
            <EditReceivableDialog
              receivable={editingReceivable}
              open={!!editingReceivable}
              onClose={() => setEditingReceivable(null)}
              onSave={() => fetchData(true)}
              users={users}
            />
          )}
        </>
      )}
    </div>
  );
}
